import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "../../styles/app/LiveBotDemo.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faStop, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { RetellWebClient } from "retell-client-js-sdk";

const retellWebClient = new RetellWebClient();

const LiveBotDemo: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "connecting" | "active">("idle");

  useEffect(() => {
    // Setup Retell event listeners
    retellWebClient.on("call_started", () => {
      console.log("[LiveBot] Call started successfully");
      setStatus("active");
    });

    retellWebClient.on("call_ended", () => {
      console.log("[LiveBot] Call ended");
      setStatus("idle");
    });

    retellWebClient.on("error", (error: any) => {
      console.error("[LiveBot] An error occurred:", error);
      setStatus("idle");
    });

    // Cleanup on unmount
    return () => {
      retellWebClient.stopCall();
    };
  }, []);

  const handleStartCall = async () => {
    setStatus("connecting");

    try {
      // 1. Fetch Session Token exactly from our backend proxy (hiding API Key)
      let baseUrl = "https://api.tuagentevirtual.ai";
      
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
        baseUrl = "https://localhost:44390"; // Reemplaza si tu backend local usa otro puerto
      }

      const apiUrl = `${baseUrl}/api/VoiceProxy/create-session`;

      const response = await fetch(apiUrl, {
        method: "POST"
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.access_token) {
        throw new Error("No access_token returned by backend.");
      }

      // 2. Start call directly using the frontend WebRTC SDK securely
      await retellWebClient.startCall({
        accessToken: data.access_token,
      });

    } catch (err) {
      console.error("Error starting live demo call:", err);
      setStatus("idle");
    }
  };

  const handleEndCall = () => {
    retellWebClient.stopCall();
    setStatus("idle");
  };

  return (
    <section className="live-bot-demo-section" id="live-demo">
      <div className="demo-content-wrapper">
        <h2 className="demo-title">
          {t("liveDemo.title", "Prueba nuestro Agente de Voz")}
        </h2>
        <p className="demo-subtitle">
          {t("liveDemo.subtitle", "Experimenta en tiempo real la naturalidad y velocidad con la que nuestra IA atiende tus peticiones.")}
        </p>

        <div className="demo-card">
          <div className="demo-card-left">
            <img 
              src="https://images.unsplash.com/photo-1596495578065-6e0763fa1178?q=80&w=800&auto=format&fit=crop" 
              alt="AI Technology" 
              className="left-hero-img" 
            />
            <div className="left-overlay">
              <h3 className="left-title">{t("liveDemo.leftTitle", "El futuro de la atención al cliente")}</h3>
              <p className="left-desc">{t("liveDemo.leftDesc", "Conversa con nuestra IA de forma natural. Sin esperas, empatía real y resolución ultrarrápida.")}</p>
            </div>
          </div>
          
          <div className="demo-card-right">
            <div className={`demo-avatar-container ${status === "active" ? "pulsing" : ""}`}>
              <img 
                className={`demo-avatar ${status}`} 
                src="/images/agent-avatar-female.jpg" 
                alt="AI Voice Agent" 
              />
              {status === "connecting" && (
                <div className="demo-avatar-overlay">
                  <FontAwesomeIcon icon={faCircleNotch} spin size="2x" color="white" />
                </div>
              )}
            </div>
            
            <div className="demo-status">
              {status === "idle" && <span>{t("liveDemo.status.idle", "Haz click para iniciar la simulación")}</span>}
              {status === "connecting" && <span className="connecting-text">{t("liveDemo.status.connecting", "Conectando segura y transparentemente...")}</span>}
              {status === "active" && <span className="active-text">{t("liveDemo.status.active", "¡Habla ahora! El Agente te escucha.")}</span>}
            </div>

            <div className="demo-controls">
              {status === "idle" && (
                <button className="btn-call start" onClick={handleStartCall}>
                  {t("liveDemo.controls.start", "Iniciar Llamada")}
                </button>
              )}
              
              {status === "connecting" && (
                <button className="btn-call cancel" onClick={handleEndCall}>
                  {t("liveDemo.controls.cancel", "Cancelar")}
                </button>
              )}

              {status === "active" && (
                <button className="btn-call stop" onClick={handleEndCall}>
                  <FontAwesomeIcon icon={faStop} /> {t("liveDemo.controls.stop", "Colgar")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveBotDemo;
